import { h } from '../lib/dom.js';
import { getState } from '../store.js';
import { iconMarkup } from '../icons/index.js';
import {
  setLockCode, unlock, unlockWithWebauthn,
  openLandingForm, setNewProfileName, setNewProfileEmail, toggleNewProfileAnonymous,
  setNewProfileCode, setNewProfileCodeConfirm, validateNewProfileEmailBlur,
  validateNewProfileCodeBlur, validateNewProfileCodeConfirmBlur,
  toggleAgreeTerms, toggleAgreeSelfCustody, submitOpenAccount, openLicense, cancelAddProfile
} from '../actions.js';

function logo(sizeClass) {
  return h('img', { class: 'logo ' + sizeClass, src: '/icons/logo.svg', alt: 'Tynti' });
}

// A real, focusable <input type="checkbox"> stays in the DOM (visually
// hidden via CSS, not removed) so keyboard/screen-reader behavior is
// unchanged — only its visible appearance is swapped for the lucide
// square/square-check icons. The icon is purely decorative (aria-hidden,
// pointer-events:none): clicking it still hits the underlying input
// because both sit inside the same <label>, which natively toggles its
// nested control on any click.
function customCheckbox(checked, onchange) {
  return h('span', { class: 'custom-checkbox-wrap' }, [
    h('input', { type: 'checkbox', class: 'custom-checkbox-input', checked, onchange }),
    h('span', { class: 'custom-checkbox-icon', 'aria-hidden': 'true', html: iconMarkup(checked ? 'squareCheck' : 'square', { size: 20, color: checked ? '#4D4DFF' : '#fff' }) })
  ]);
}

// Only shown when adding a profile from an already-unlocked Home session —
// a brand-new device has nowhere else to go, but this entry point does.
function backButton(s) {
  if (!s.landingAddingProfile) return null;
  return h('button', { class: 'icon-btn landing-back', onclick: cancelAddProfile }, [
    h('span', { html: iconMarkup('arrowLeft', { size: 14 }) }), ' Back'
  ]);
}

function faq() {
  return h('div', { class: 'faq stack mt-md' }, [
    h('details', { class: 'faq-item' }, [
      h('summary', {}, 'What is Tynti?'),
      h('div', { class: 'faq-answer' }, "A self-custody stablecoin wallet for holding, receiving, and moving your company's digital dollars — like a bank account, without a bank.")
    ]),
    h('details', { class: 'faq-item' }, [
      h('summary', {}, 'Who holds my funds?'),
      h('div', { class: 'faq-answer' }, 'You do. Your keys are generated and kept on this device only — Tynti never has access to your funds.')
    ]),
    h('details', { class: 'faq-item' }, [
      h('summary', {}, 'What license is this software under?'),
      h('div', { class: 'faq-answer' }, [
        'Tynti is provided under the ',
        h('a', { href: '#', onclick: (e) => { e.preventDefault(); openLicense(); } }, 'MIT license'),
        ' — free and open to use, copy, and modify, provided "as is" with no warranty.'
      ])
    ])
  ]);
}

function renderWelcomeBack(s) {
  const profile = s.profiles.find((p) => p.id === s.activeProfileId) || s.profiles[0];
  const input = h('input', {
    class: 'lock-input',
    value: s.lockCode,
    placeholder: '6-digit code',
    maxlength: '6',
    inputmode: 'numeric',
    oninput: (e) => setLockCode(e.target.value)
  });
  input.addEventListener('keydown', (e) => { if (e.key === 'Enter') unlock(); });

  // Biometric unlock only shows when this device *and* this specific
  // profile both support it — the button is a convenience layer over the
  // same vault the PIN below unwraps (see vault.js), never a replacement
  // for it, so the PIN field is always present regardless.
  const canWebauthn = s.webauthnAvailable && profile && profile.vault && profile.vault.webauthn;

  return h('div', { class: 'dark-screen' }, [
    logo('logo-sm'),
    h('div', { class: 'label' }, profile ? `Welcome back, ${profile.name}` : 'Welcome back'),
    canWebauthn ? h('button', {
      class: 'btn btn-primary btn-lg', disabled: s.lockChecking, onclick: unlockWithWebauthn
    }, [
      h('span', { html: iconMarkup('scanFace', { size: 18 }) }), ' ', s.lockChecking ? 'Checking…' : 'Unlock with biometrics'
    ]) : null,
    h('div', { class: 'copy' }, canWebauthn ? 'or enter your passcode' : 'Enter your passcode'),
    input,
    h('div', { class: 'lock-error' }, s.lockError || ''),
    h('button', {
      class: 'btn btn-lg ' + (canWebauthn ? 'btn-secondary' : 'btn-primary'),
      disabled: s.lockChecking, onclick: unlock
    }, s.lockChecking ? 'Checking…' : 'Unlock')
  ]);
}

function renderNewAccountIntro(s) {
  return h('div', { class: 'dark-screen' }, [
    backButton(s),
    logo('logo-lg'),
    h('div', { class: 'copy' }, "Stablecoin treasury, simplified. Hold, receive, and move your company's digital dollars like a bank account."),
    h('button', { class: 'btn btn-primary btn-lg', onclick: openLandingForm }, 'Instant account opening'),
    faq()
  ]);
}

function renderNewAccountForm(s) {
  const errors = s.newProfileErrors;

  const nameInput = h('input', {
    placeholder: 'Your name / company name',
    value: s.newProfileName,
    disabled: s.newProfileAnonymous,
    oninput: (e) => setNewProfileName(e.target.value)
  });
  const emailInput = h('input', {
    type: 'email',
    placeholder: 'Email address (optional)',
    value: s.newProfileEmail,
    disabled: s.newProfileAnonymous,
    oninput: (e) => setNewProfileEmail(e.target.value),
    onblur: validateNewProfileEmailBlur
  });
  const anonRow = h('label', { class: 'checkbox-row' }, [
    customCheckbox(s.newProfileAnonymous, toggleNewProfileAnonymous),
    h('span', {}, 'Anonymous')
  ]);

  const codeInput = h('input', {
    class: 'code-input',
    placeholder: '6-digit passcode',
    maxlength: '6',
    inputmode: 'numeric',
    value: s.newProfileCode,
    oninput: (e) => setNewProfileCode(e.target.value),
    onblur: validateNewProfileCodeBlur
  });
  const codeConfirmInput = h('input', {
    class: 'code-input',
    placeholder: 'Repeat passcode',
    maxlength: '6',
    inputmode: 'numeric',
    value: s.newProfileCodeConfirm,
    oninput: (e) => setNewProfileCodeConfirm(e.target.value),
    onblur: validateNewProfileCodeConfirmBlur
  });

  const termsRow = h('label', { class: 'checkbox-row' }, [
    customCheckbox(s.agreeTerms, toggleAgreeTerms),
    h('span', {}, [
      'I agree to the ',
      h('a', { href: '#', onclick: (e) => { e.preventDefault(); openLicense(); } }, 'MIT license'),
      ' to use this software'
    ])
  ]);
  const selfCustodyRow = h('label', { class: 'checkbox-row' }, [
    customCheckbox(s.agreeSelfCustody, toggleAgreeSelfCustody),
    h('span', {}, [
      'I understand this is a self-custody solution, provided ',
      h('a', { href: '#', onclick: (e) => { e.preventDefault(); openLicense(); } }, '"AS IS"'),
      ', used at my own risk.'
    ])
  ]);

  return h('div', { class: 'dark-screen dark-screen-scroll' }, [
    backButton(s),
    logo('logo-sm'),
    h('div', { class: 'label' }, 'Open your account'),
    h('div', { class: 'field stack' }, [nameInput, emailInput, anonRow]),
    h('div', { class: 'field-error' }, errors.name || errors.email || ''),
    h('div', { class: 'field stack' }, [codeInput, codeConfirmInput]),
    h('div', { class: 'field-error' }, errors.code || ''),
    h('div', { class: 'stack' }, [termsRow, selfCustodyRow]),
    h('div', { class: 'field-error' }, errors.agree || ''),
    h('button', {
      class: 'btn btn-primary btn-lg',
      disabled: s.newProfileSubmitting,
      onclick: s.newProfileSubmitting ? null : submitOpenAccount
    }, s.newProfileSubmitting ? 'Opening…' : 'Open Account'),
    faq()
  ]);
}

export function renderLanding() {
  const s = getState();
  if (s.profiles.length > 0 && !s.landingAddingProfile) return renderWelcomeBack(s);
  if (!s.landingFormOpen) return renderNewAccountIntro(s);
  return renderNewAccountForm(s);
}
